import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {selectActiveVehicle, selectProfile} from '@ox/data';
import {Profile, Vehicle} from '@ox/typings';
import {SessionStorageService} from '@ox/utils';
import {find, forEach} from 'lodash';
import {combineLatest, of, switchMap} from 'rxjs';
import {CopyFormattingService, ShopDataService} from '@webu/data';
import {CheckoutDataService} from './checkout-data.service';
import {OfferService} from './offer.service';

@Injectable()
export class CheckoutService {
  activeVehicle: Vehicle | undefined;
  profile: Profile | undefined;
  reviewInfo: any;

  constructor(
    private http: HttpClient,
    private store: Store,
    private ocSessionStorageService: SessionStorageService,
    private copyService: CopyFormattingService,
    private offerService: OfferService,
    private checkoutDataService: CheckoutDataService,
    private shopDataService: ShopDataService
  ) {
  }

  checkout(offerId: string) {
    combineLatest([
      this.store.select(selectActiveVehicle),
      this.store.select(selectProfile)
    ]).subscribe(([activeVehicle, profile]) => {
      this.activeVehicle = activeVehicle;
      this.profile = profile;
      combineLatest([
        this.getReviewPlanInfo(
          this.profile?.profileId,
          this.activeVehicle?.vin,
          [offerId]
        ),
        this.shopDataService.getCopyData(),
        this.offerService.getOfferByProductId(offerId, this.activeVehicle?.vin)
      ]).subscribe({
        next: (response) => {
          const reviewInfo = response[0];
          const copy = response[1];
          const offers = response[2];
          const packOffers = this.packageOffers(offers, copy, reviewInfo);
          const priceDisclosure = this.getDisclosure(copy);
          const checkoutObject = {
            offers: [packOffers],
            displayChatNow: false,
            priceDisclosureCopy: priceDisclosure.copyDocValue,
            promoPeriodCopy: this.reviewInfo.promoPeriodCopy,
            upcomingSubscription:
              this.reviewInfo.upcomingSubscription,
            currentSubscription: this.reviewInfo.currentSubscription,
            pageType: 'REVIEW',
            alerts: this.reviewInfo.alerts
          };
          this.checkoutDataService
            .checkout(checkoutObject, this.activeVehicle)
            .subscribe((item) => {
              location.href = item;
            });
        },
        error: (err) => {
          console.log(err);
        }
      });
    });
  }

  getDisclosure(shopCopy: any) {
    return find(shopCopy.generic, (copy: any) => {
      return copy.copyDocKey === 'price.disclosure.modal';
    });
  }

  packageOffers(offer: any, copy: any, reviewInfo: any) {
    const products = this.packageProducts(offer.products, copy.products);
    return {
      offerId: offer.id,
      offerName: reviewInfo.upcomingSubscription.plans[0].id,
      offerDescription: offer.shortDescription,
      offerType: offer.category,
      products: products,
      disclaimerCodes: offer.disclaimerCodes,
      duration: offer.price.duration
    };
  }

  packageProducts(products: any, copies: any) {
    const mutatedProducts: any[] = [];
    forEach(products, (product: any) => {
      let matchingCopy;
      //Use the code property for most accurate match, if not available, then fall back to the old implementation
      if (product.productId) {
        matchingCopy = find(copies, (copy: any) => {
          return copy.code === product.productId;
        });
      } else {
        matchingCopy = find(copies, (copy: any) => {
          return copy.name === product.name || copy.name === product.productId;
        });
      }
      if (matchingCopy) {
        product.type = matchingCopy.type;
        product.offeredBy = matchingCopy.offeredBy;
        product.disclosure = matchingCopy.disclosure;
        this.ocSessionStorageService.setAsJson(
          'helpRefs',
          matchingCopy.helpContentRefs,
          true
        );
      }
      mutatedProducts.push(product);
    });
    return mutatedProducts;
  }

  getReviewInfo = (offerId: string) => {
    this.ocSessionStorageService.setAsJson('ReviewId', offerId);
    return this.getReviewPlanInfo(
      this.profile?.onStarAcctId,
      this.activeVehicle?.vin,
      offerId
    );
  };

  getReviewRequest(num: any, vin: any, offers: any) {
    return {
      accountNumber: num,
      vin: vin,
      offerIds: offers
    };
  }

  // TODO: Move this to shop-data.service once the Review API can be called through the b2c gateway
  getReviewPlanInfo(
    acctId: string | undefined,
    vin: any,
    offers: any
  ) {
    const reviewRequest = this.getReviewRequest(acctId, vin, offers);
    const reviewEndpoint = '/onStar/shop/review';
    const reviewInfo: any = this.ocSessionStorageService.getFromJson(
      'ReviewInfo',
      true
    );
    if (reviewInfo && reviewInfo.offers === offers) {
      return of(reviewInfo);
    } else {
      return this.http.get(reviewEndpoint, {headers: reviewRequest}).pipe(
        switchMap((response: any) => {
          const reviewInfo = response.data;
          reviewInfo.offers = offers;
          const copyInfo = response[1];

          if (
            reviewInfo &&
            reviewInfo.currentSubscription &&
            reviewInfo.currentSubscription.plans &&
            copyInfo &&
            copyInfo.products
          ) {
            reviewInfo.currentSubscription.plans =
              this.copyService.matchProductsById(
                reviewInfo.currentSubscription.plans,
                copyInfo.products
              );
          }
          if (
            reviewInfo &&
            reviewInfo.upcomingSubscription &&
            reviewInfo.upcomingSubscription.plans &&
            copyInfo &&
            copyInfo.products
          ) {
            reviewInfo.upcomingSubscription.plans =
              this.copyService.matchProductsById(
                reviewInfo.upcomingSubscription.plans,
                copyInfo.products
              );
          }

          this.ocSessionStorageService.setAsJson(
            'ReviewInfo',
            reviewInfo,
            true
          );
          this.reviewInfo = reviewInfo;
          return of(reviewInfo);
        })
      );
    }
  }
}
